/**
 * Departments, categories and document types.
 *
 * Policies need somewhere to live, so this is the second thing a new customer
 * sets up after inviting people. All four kinds share one shape — a name, an
 * optional short code, an order and a colour — so they share one hook rather
 * than four near-identical copies.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { call } from '../../lib/callable';

/** The taxonomies this page manages, and the callables behind each. */
export const TAXONOMY_KINDS = ['department', 'category', 'documentType'] as const;
export type TaxonomyKind = (typeof TAXONOMY_KINDS)[number];

interface KindConfig {
  /** Plural, for headings. */
  label: string;
  /** Singular, for buttons and messages. */
  singular: string;
  list: string;
  upsert: string;
  remove: string;
  /** The field the list callable returns them under. */
  listField: string;
  /** The id field the upsert and delete callables expect. */
  idField: string;
  /** What this taxonomy is for, in one line. */
  hint: string;
}

export const KINDS: Record<TaxonomyKind, KindConfig> = {
  department: {
    label: 'Departments',
    singular: 'department',
    list: 'getDepartments',
    upsert: 'upsertDepartment',
    remove: 'deleteDepartment',
    listField: 'departments',
    idField: 'deptId',
    hint: 'Who owns a policy, and whose staff are asked to acknowledge it.',
  },
  category: {
    label: 'Categories',
    singular: 'category',
    list: 'getCategories',
    upsert: 'upsertCategory',
    remove: 'deleteCategory',
    listField: 'categories',
    idField: 'catId',
    hint: 'What a policy is about — infection control, medication, safety.',
  },
  documentType: {
    label: 'Document types',
    singular: 'document type',
    list: 'getDocumentTypes',
    upsert: 'upsertDocumentType',
    remove: 'deleteDocumentType',
    listField: 'documentTypes',
    idField: 'docTypeId',
    hint: 'Policy, procedure, guideline, form — how the document is governed.',
  },
};

export interface TaxonomyItem {
  id: string;
  name: string;
  code: string | null;
  /**
   * The short code policy NUMBERING draws on, which is a different field from
   * `code` and takes precedence over it: policyNumberFunctions reads
   * `policyCode || code`. Departments and categories have it; document types
   * use their `code` for the same job.
   */
  policyCode: string | null;
  color: string | null;
  sortOrder: number;
  isActive: boolean;
}

/** What still points at an item. `null` means the number is not known. */
export interface TaxonomyUsage {
  policies: number | null;
  workflows: number | null;
  staff: number | null;
  unused: boolean;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v ? v : null;
}

function toItem(raw: unknown): TaxonomyItem | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const d = raw as Record<string, unknown>;
  const id = str(d.id) ?? str(d.deptId) ?? str(d.catId) ?? str(d.docTypeId);
  const name = str(d.name);
  if (!id || !name) return null;

  return {
    id,
    name,
    code: str(d.code),
    policyCode: str(d.policyCode),
    color: str(d.color),
    sortOrder: typeof d.sortOrder === 'number' ? d.sortOrder : 0,
    // Absent means active. Only an explicit false deactivates, so an older
    // document without the field does not vanish from the list.
    isActive: d.isActive !== false,
  };
}

export function useTaxonomy(kind: TaxonomyKind, enabled: boolean) {
  const config = KINDS[kind];

  return useQuery({
    queryKey: ['taxonomy', kind],
    enabled,
    queryFn: async (): Promise<TaxonomyItem[]> => {
      const result = await call<Record<string, unknown>, Record<string, unknown>>(
        config.list,
        { includeInactive: true },
      );
      const list = result[config.listField];
      if (!Array.isArray(list)) return [];
      return list
        .map(toItem)
        .filter((item): item is TaxonomyItem => item !== null)
        // Alphabetical. `sortOrder` was a creation counter nothing ever set
        // deliberately, so ordering by it put a list of thirty departments in
        // the order somebody happened to type them.
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  });
}

function useTaxonomyMutation<TInput>(kind: TaxonomyKind, name: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) => call<TInput, unknown>(name, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['taxonomy', kind] });
    },
  });
}

export function useUpsertTaxonomy(kind: TaxonomyKind) {
  return useTaxonomyMutation<Record<string, unknown>>(kind, KINDS[kind].upsert);
}

export function useDeleteTaxonomy(kind: TaxonomyKind) {
  return useTaxonomyMutation<Record<string, unknown>>(kind, KINDS[kind].remove);
}

/**
 * What every item of a kind is used by.
 *
 * Its own query rather than part of the list, because it costs two aggregate
 * queries per item against the policies collection and the list must render
 * before it arrives. A row shows its counts when they land.
 */
export interface UsageResult {
  usage: Record<string, TaxonomyUsage>;
  counted: number;
  total: number;
  complete: boolean;
  workflowsKnown: boolean;
  staffKnown: boolean;
}

export function useTaxonomyUsage(kind: TaxonomyKind, enabled: boolean) {
  return useQuery({
    queryKey: ['taxonomy-usage', kind],
    enabled,
    // Counts move when policies move, not when somebody opens a tab.
    staleTime: 60 * 1000,
    queryFn: async (): Promise<UsageResult> => {
      const raw = await call<{ kind: TaxonomyKind }, {
        usage: Array<{ id: string; policies: number | null; workflows: number | null; staff: number | null; unused: boolean }>;
        counted: number; total: number; complete: boolean; workflowsKnown: boolean; staffKnown: boolean;
      }>('getTaxonomyUsage', { kind });

      const usage: Record<string, TaxonomyUsage> = {};
      for (const row of raw.usage ?? []) {
        usage[row.id] = { policies: row.policies, workflows: row.workflows, staff: row.staff, unused: row.unused };
      }
      return {
        usage,
        counted: raw.counted ?? 0,
        total: raw.total ?? 0,
        complete: raw.complete !== false,
        workflowsKnown: raw.workflowsKnown !== false,
        staffKnown: raw.staffKnown !== false,
      };
    },
  });
}

/** Archive or reactivate. The id does not move, so neither does anything else. */
export function useSetTaxonomyActive(kind: TaxonomyKind) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; isActive: boolean }) =>
      call<{ kind: TaxonomyKind; id: string; isActive: boolean }, { stillUsedBy?: string }>(
        'setTaxonomyItemActive', { kind, ...input },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['taxonomy', kind] });
      void queryClient.invalidateQueries({ queryKey: ['taxonomy-usage', kind] });
    },
  });
}

/**
 * The counts behind a confirmation, read at the moment of asking.
 *
 * The list's counts may be a minute old. What an administrator is shown before
 * a destructive choice should not be, and the callable that carries it out
 * asks a third time for itself.
 */
export function useItemUsage(kind: TaxonomyKind) {
  return useMutation({
    mutationFn: (id: string) =>
      call<{ kind: TaxonomyKind; id: string }, TaxonomyUsage & { name: string; summary: string }>(
        'getTaxonomyItemUsage', { kind, id },
      ),
  });
}
